/*
 * Mounts the loyalty programme's referral widget inside the product page modal.
 *
 * The loyalty app's loader is injected on every page by its app embed and
 * mounts any `.yotpo-widget-instance` placeholder it finds. The placeholder
 * inside the modal is present from the first render, so in most cases the
 * widget is already there by the time the modal opens; when the loader lands
 * late, or leaves the placeholder empty because the modal was hidden, the
 * widget is asked to mount again the first time a shopper opens the modal.
 */
if (!customElements.get('refer-a-friend-widget')) {
  class ReferAFriendWidget extends HTMLElement {
    constructor() {
      super();
      this.placeholder = this.querySelector('.yotpo-widget-instance');
      this.status = this.querySelector('[data-refer-a-friend-status]');
      this.requested = false;
      this.attempts = 0;
    }

    connectedCallback() {
      if (!this.placeholder) return;

      this.watchPlaceholder();

      this.modal = this.closest('modal-dialog');
      if (!this.modal) return;

      this.modalObserver = new MutationObserver(() => {
        if (this.modal.hasAttribute('open')) {
          this.mount();
          this.watchContent();
        } else {
          this.stopWatchingContent();
        }
      });
      this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });

      if (this.modal.hasAttribute('open')) this.mount();
    }

    disconnectedCallback() {
      if (this.modalObserver) this.modalObserver.disconnect();
      if (this.placeholderObserver) this.placeholderObserver.disconnect();
      if (this.timer) clearTimeout(this.timer);
      this.stopPolling();
      this.stopWatchingContent();
    }

    /*
     * The live placeholder. The loyalty loader does not always mount into the
     * node this element captured at construction: it may replace it, or re-create
     * it, so the captured node ends up detached while a mounted widget sits in a
     * fresh one. Re-resolve it from the DOM every time, and keep the captured
     * reference in step so `initWidget` hands the loader a node that is still in
     * the document.
     */
    get widgetRoot() {
      const current = this.querySelector('.yotpo-widget-instance, [data-yotpo-instance-id]');
      if (current) this.placeholder = current;
      return this.placeholder && this.placeholder.isConnected ? this.placeholder : null;
    }

    /*
     * "Rendered" means the widget put something on the screen, whatever shape it
     * took: light-DOM children, a shadow root (which no MutationObserver on this
     * element can see), or text. A signed-out widget showing JOIN NOW / LOG IN
     * counts. Anything the loader left alongside the placeholder counts too — the
     * status paragraph is this element's own and is excluded.
     */
    get rendered() {
      const root = this.widgetRoot;
      if (root && (root.childElementCount > 0 || root.shadowRoot || root.textContent.trim().length > 0)) {
        return true;
      }

      return Array.from(this.children).some(
        (child) =>
          child !== this.status &&
          child !== root &&
          (child.childElementCount > 0 || child.shadowRoot || child.textContent.trim().length > 0)
      );
    }

    watchPlaceholder() {
      if (this.placeholderObserver) this.placeholderObserver.disconnect();

      if (this.rendered) {
        this.settle();
        return;
      }

      // Observe this element rather than the placeholder, so a mount that
      // replaces the placeholder node is still seen.
      this.placeholderObserver = new MutationObserver(() => {
        if (this.rendered) this.settle();
      });
      this.placeholderObserver.observe(this, { childList: true, subtree: true });

      // A widget that mounts into a shadow root mutates nothing an observer can
      // reach, so poll alongside it until it settles or the mount times out.
      this.startPolling();
    }

    startPolling() {
      this.stopPolling();
      this.pollTicks = 0;
      this.poller = setInterval(() => {
        this.pollTicks += 1;
        if (this.rendered) {
          this.settle();
        } else if (this.pollTicks > 40) {
          // Past the mount timeout the observer is enough; stop burning a timer.
          this.stopPolling();
        }
      }, 500);
    }

    stopPolling() {
      if (this.poller) {
        clearInterval(this.poller);
        this.poller = null;
      }
    }

    settle() {
      if (this.placeholderObserver) this.placeholderObserver.disconnect();
      if (this.timer) clearTimeout(this.timer);
      this.stopPolling();
      this.removeAttribute('failed');
      this.setAttribute('loaded', '');
      if (this.status) {
        this.status.remove();
        this.status = null;
      }
      this.syncFocusTrap();
      this.watchContent();
    }

    /*
     * The theme's focus trap snapshots the first and last focusable element when
     * the modal opens. The widget mounts after that, so those boundaries have to
     * be recomputed once its content is in the DOM, and again whenever the widget
     * swaps its own steps. Focus is left where it is so the shopper is not moved.
     *
     * The theme's `trapFocus` re-focuses the element it is handed and selects the
     * whole value of a text or email input, which would wipe what a shopper is
     * part-way through typing. So while focus sits in a field, the boundaries are
     * left alone, and otherwise they are recomputed without moving focus.
     */
    syncFocusTrap() {
      if (typeof trapFocus !== 'function') return;
      if (!this.modal || !this.modal.hasAttribute('open')) return;

      const dialog = this.modal.querySelector('[role="dialog"]');
      if (!dialog) return;

      const active = document.activeElement;
      if (!active || !this.modal.contains(active)) return;

      if (active.matches('input, textarea, select') || active.isContentEditable) return;

      trapFocus(this.modal, active === this.modal ? dialog : active);
    }

    watchContent() {
      if (this.contentObserver || !this.rendered) return;
      if (!this.modal || !this.modal.hasAttribute('open')) return;

      this.contentObserver = new MutationObserver(() => {
        if (this.contentTimer) clearTimeout(this.contentTimer);
        this.contentTimer = setTimeout(() => this.syncFocusTrap(), 200);
      });
      this.contentObserver.observe(this.placeholder, { childList: true, subtree: true });
    }

    stopWatchingContent() {
      if (this.contentObserver) {
        this.contentObserver.disconnect();
        this.contentObserver = null;
      }
      if (this.contentTimer) clearTimeout(this.contentTimer);
    }

    mount() {
      if (this.requested || this.rendered) return;
      this.requested = true;
      this.restoreLoadingState();
      this.initWidget();
    }

    restoreLoadingState() {
      if (!this.hasAttribute('failed')) return;
      this.removeAttribute('failed');
      if (this.status && this.status.dataset.loadingText) {
        this.status.textContent = this.status.dataset.loadingText;
      }
    }

    initWidget() {
      const container = window.yotpoWidgetsContainer;
      const instanceId = this.dataset.instanceId;
      const target = this.widgetRoot;

      if (container && typeof container.initWidget === 'function' && instanceId && target) {
        try {
          container.initWidget(instanceId, target);
        } catch (e) {
          console.error(e);
          this.fail();
          return;
        }
        this.waitForRender();
        return;
      }

      if (container && typeof container.initWidgets === 'function') {
        try {
          container.initWidgets();
        } catch (e) {
          console.error(e);
          this.fail();
          return;
        }
        this.waitForRender();
        return;
      }

      this.attempts += 1;
      if (this.attempts > 40) {
        this.fail();
        return;
      }

      this.timer = setTimeout(() => this.initWidget(), 250);
    }

    waitForRender() {
      if (this.timer) clearTimeout(this.timer);
      this.startPolling();
      this.timer = setTimeout(() => {
        // Only an empty placeholder is a failure. A widget that mounted while the
        // timer ran settles instead, so the error is never shown over content.
        if (this.rendered) {
          this.settle();
        } else {
          this.fail();
        }
      }, 10000);
    }

    /*
     * A failed mount is not final: the loyalty loader may still arrive. The
     * request is released and the placeholder watched again so the next time the
     * shopper opens the modal the widget is asked to mount once more.
     */
    fail() {
      if (this.timer) clearTimeout(this.timer);
      if (this.rendered) {
        this.settle();
        return;
      }
      this.stopPolling();
      this.setAttribute('failed', '');
      if (this.status) {
        this.status.textContent = this.status.dataset.errorText || this.status.textContent;
      }
      this.syncFocusTrap();

      this.requested = false;
      this.attempts = 0;
      this.watchPlaceholder();
    }
  }

  customElements.define('refer-a-friend-widget', ReferAFriendWidget);
}
